import {
  CheckFilledIcon,
  DismissFilledIcon,
  Flex,
  Typography,
} from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import type {
  ConditionSection,
  ConditionSelection,
  ConditionSelectionType,
} from './conditions.types';
import {
  CONDITION_SELECTION_TYPES,
  getConditionSummaryRows,
} from './util/conditionSelection.util';

import styles from './conditionSummary.module.css';

export interface ConditionSummaryProps {
  /** The sections the group was built from, used to label its selections */
  conditions: ConditionSection[];

  /** The selections belonging to the group being deleted */
  selection: ConditionSelection;
}

const SELECTION_TYPE_HEADINGS: Record<ConditionSelectionType, string> = {
  excludes: 'Excludes',
  includes: 'Includes',
};

const SELECTION_TYPE_ICONS: Record<ConditionSelectionType, ReactNode> = {
  excludes: <DismissFilledIcon size="sm" color="error" />,
  includes: <CheckFilledIcon size="sm" />,
};

const toMoreLabel = (count: number) => `+${count} more`;

/**
 * Fills the row with every value, cut off with an ellipsis, and counts the ones
 * that never start into "+N more". Widths come from a hidden copy of each value;
 * with no layout (width 0) nothing is counted.
 */
const SummaryValues = ({ values }: { values: string[] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLParagraphElement>(null);
  const [shownCount, setShownCount] = useState(values.length);
  const count = values.length;
  /** The rows are rebuilt every render, so the effect keys on content, not identity */
  const valuesKey = values.join('\u0000');

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;

    if (!container || !measure) {
      return;
    }

    const update = () => {
      const available = container.clientWidth;
      const [more, ...items] = Array.from(measure.children) as HTMLElement[];

      if (!available || !more) {
        return setShownCount(count);
      }

      const gap = 8;
      const textWidth = (itemCount: number) =>
        items
          .slice(0, itemCount)
          .reduce((sum, item) => sum + item.offsetWidth, 0);

      if (textWidth(count) <= available) {
        return setShownCount(count);
      }

      // "+N more" is widest at the largest N, so reserve for that
      const room = available - more.offsetWidth - gap;
      // A value counts as shown once it starts before the cut-off
      const shown = items.filter((_, index) => textWidth(index) < room).length;

      setShownCount(Math.max(shown, 1));
    };

    update();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(update);

    observer.observe(container);

    return () => observer.disconnect();
  }, [count, valuesKey]);

  const hidden = values.length - shownCount;

  return (
    <Flex
      basis="0%"
      gap={8}
      grow
      minWidth={0}
      position="relative"
      ref={containerRef}
    >
      <Typography
        color="grays-mid"
        maxLines={1}
        minWidth={0}
        title={values.join(', ')}
      >
        {values.join(', ')}
      </Typography>
      {hidden > 0 && (
        <Typography color="grays-mid" whiteSpace="nowrap">
          {toMoreLabel(hidden)}
        </Typography>
      )}
      {/* Typography so the copy measures in the same font as the text */}
      <p aria-hidden className={styles['measure']} ref={measureRef}>
        <Typography as="span" whiteSpace="pre">
          {toMoreLabel(values.length)}
        </Typography>
        {/* Labels can repeat (same-named products), and these never reorder */}
        {values.map((value, index) => (
          <Typography as="span" key={index} whiteSpace="pre">
            {`${value}, `}
          </Typography>
        ))}
      </p>
    </Flex>
  );
};

export const ConditionSummary = ({
  conditions,
  selection,
}: ConditionSummaryProps) => {
  const rows = getConditionSummaryRows(conditions, selection);

  if (rows.length === 0) {
    return <Typography>This group has no conditions selected.</Typography>;
  }

  return (
    <Flex flexDirection="column" gap={16} width="100%">
      {CONDITION_SELECTION_TYPES.map((selectionType) => {
        const typeRows = rows.filter(
          (row) => row.selectionType === selectionType
        );

        return (
          typeRows.length > 0 && (
            <Flex flexDirection="column" gap={4} key={selectionType}>
              <Flex alignItems="center" gap={4}>
                {SELECTION_TYPE_ICONS[selectionType]}
                <Typography color="grays-mid" variant="caps-regular">
                  {SELECTION_TYPE_HEADINGS[selectionType]}
                </Typography>
              </Flex>
              <Flex
                aria-label={SELECTION_TYPE_HEADINGS[selectionType]}
                as="ul"
                flexDirection="column"
                gap={2}
              >
                {typeRows.map((row) => (
                  <Flex alignItems="center" as="li" gap={8} key={row.label}>
                    <Typography variant="body-semibold" whiteSpace="nowrap">
                      {row.label}
                    </Typography>
                    <SummaryValues values={row.values} />
                  </Flex>
                ))}
              </Flex>
            </Flex>
          )
        );
      })}
    </Flex>
  );
};
